import {
  Box,
  Button,
  Dialog,
  Select,
  Text,
  TextField,
  Tooltip,
} from '@radix-ui/themes';
import { Pen } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import {
  EditPOSDeviceRequest,
  POSDeviceResponse,
} from '../../model/POS-device.model';
import POSDeviceService from '../../service/pos-device-service/POS-device.service';
import useBreakpoint from 'antd/es/grid/hooks/useBreakpoint';

interface EditPOSDeviceModalProps {
  posDevice: POSDeviceResponse;
  onSuccess: () => Promise<void>;
}

const EditPOSDeviceModal: React.FC<EditPOSDeviceModalProps> = ({
  posDevice,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState<EditPOSDeviceRequest>({
    deviceCode: posDevice.deviceCode,
    location: posDevice.location,
    status: posDevice.status,
  });
  const screens = useBreakpoint();

  useEffect(() => {
    setFormData({
      deviceCode: posDevice.deviceCode,
      location: posDevice.location,
      status: posDevice.status,
    });
  }, [posDevice]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await POSDeviceService.editPOSDevice(posDevice.posId, formData);
      onSuccess();
      setOpen(false);
    } catch (error) {
      console.error('Error updating POS device:', error);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger>
        <Button variant="ghost" size="1" className="cursor-pointer">
          <Tooltip content="Sửa">
            <Pen size={18} />
          </Tooltip>
        </Button>
      </Dialog.Trigger>

      <Dialog.Content style={{ maxWidth: screens.xs ? '95%' : 500 }}>
        <Dialog.Title>Chỉnh sửa</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Chỉnh sửa thông tin thiết bị
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Box className="space-y-4">
            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Mã thiết bị <span className="text-red-500">*</span>
              </Text>
              <TextField.Root
                name="deviceCode"
                placeholder="Nhập mã thiết bị"
                value={formData.deviceCode}
                onChange={handleChange}
                required
                className="w-full"
              />
            </div>

            <div>
              <Text as="label" size="2" mb="1" weight="bold">
                Địa điểm
              </Text>
              <TextField.Root
                name="location"
                placeholder="Nhập địa điểm"
                value={formData.location}
                onChange={handleChange}
                className="w-full"
              />
            </div>

            <div className="flex flex-col gap-1">
              <Text as="label" size="2" weight="bold">
                Trạng thái
              </Text>
              <Select.Root
                name="status"
                value={formData.status}
                onValueChange={(value) => {
                  setFormData((prev) => ({
                    ...prev,
                    status: value,
                  }));
                }}
              >
                <Select.Trigger
                  placeholder="Select status"
                  className="w-full cursor-pointer"
                />
                <Select.Content position="popper">
                  <Select.Group>
                    <Select.Item value="ACTIVE">Hoạt động</Select.Item>
                    <Select.Item value="INACTIVE">Không hoạt động</Select.Item>
                  </Select.Group>
                </Select.Content>
              </Select.Root>
            </div>
          </Box>

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
            <Dialog.Close>
              <Button
                variant="soft"
                color="gray"
                className="cursor-pointer w-full sm:w-auto"
              >
                Hủy
              </Button>
            </Dialog.Close>
            <Button
              color="blue"
              variant="soft"
              type="submit"
              className="cursor-pointer w-full sm:w-auto"
            >
              Hoàn thành
            </Button>
          </div>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export default EditPOSDeviceModal;
